import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Label2, StatusBadge, CRIT_TONE } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Siren, Lightning, MapPin, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_TONE = { gemeldet: "gray", priorisiert: "amber", disponiert: "blue", vor_ort: "amber", geloest: "green" };
const STATUS_LABEL = { gemeldet: "Gemeldet", priorisiert: "Priorisiert", disponiert: "Disponiert", vor_ort: "Vor Ort", geloest: "Gelöst" };

export default function Notdienst() {
  const [items, setItems] = useState(null);
  const [assets, setAssets] = useState([]);
  const [oncall, setOncall] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ asset_id: "", customer: "", description: "" });
  const [onsite, setOnsite] = useState(null);
  const [fm, setFm] = useState("");

  const load = useCallback(() => {
    api.get("/incidents").then((r) => setItems(r.data));
    api.get("/assets").then((r) => setAssets(r.data));
    api.get("/oncall").then((r) => setOncall(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try { await api.post("/incidents", { ...form, asset_id: form.asset_id || null }); toast.success("Störung gemeldet"); setOpen(false); setForm({ asset_id: "", customer: "", description: "" }); load(); }
    catch (e) { toast.error("Fehler", { description: formatApiError(e.response?.data?.detail) }); }
  };
  const prioritize = async (id) => { const { data } = await api.post(`/incidents/${id}/prioritize`); toast.success(`Priorität: ${data.priority}`); load(); };
  const dispatch = async (id, assignee) => { await api.post(`/incidents/${id}/dispatch`, { assignee }); toast.success("Disponiert"); load(); };
  const doOnsite = async () => { await api.post(`/incidents/${onsite}/onsite`, { first_measures: fm }); toast.success("Erstsicherung erfasst"); setOnsite(null); setFm(""); load(); };
  const resolve = async (id) => { const { data } = await api.post(`/incidents/${id}/resolve`, { note: "Behoben", convert_to_repair: true }); toast.success("Behoben", { description: data.repair_id ? "Reparatur angelegt" : "" }); load(); };

  if (!items) return <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-md" />)}</div>;

  return (
    <div>
      <PageHeader title="Notdienst" subtitle="Zeitkritische Störungen · Priorisierung · Bereitschaft · SLA">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-incident-button" className="gap-2"><Plus size={18} weight="bold" />Störung melden</Button></DialogTrigger>
          <DialogContent data-testid="incident-dialog"><DialogHeader><DialogTitle className="font-heading">Störung melden</DialogTitle></DialogHeader>
            <DialogDescription>Notdienst-Vorgang erfassen.</DialogDescription>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label2>Anlage</Label2>
                <Select value={form.asset_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, asset_id: v === "none" ? "" : v }))}>
                  <SelectTrigger><SelectValue placeholder="Keine" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Keine</SelectItem>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label2>Kunde</Label2><Input value={form.customer} onChange={(e) => setForm((f) => ({ ...f, customer: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label2>Beschreibung *</Label2><Textarea data-testid="incident-description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
            </div>
            <DialogFooter><Button data-testid="incident-save" onClick={submit} disabled={!form.description}>Melden</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="space-y-4">
        {items.length === 0 && <Card className="rounded-md border p-8 text-center text-sm text-muted-foreground">Keine Störungen.</Card>}
        {items.map((i) => (
          <Card key={i.id} data-testid="incident-row" className={`rounded-md border p-5 ${i.sla_breached ? "border-danger/50" : ""}`}>
            <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Siren size={18} weight="fill" className="text-danger" />
                  <span className="font-semibold">{i.asset_name || i.customer || "Störung"}</span>
                  {i.sla_breached && <StatusBadge tone="red" testid="sla-breached"><Warning size={12} className="mr-1 inline" />SLA überschritten</StatusBadge>}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{i.description}</p>
                {i.sla_deadline && <p className="mt-1 text-xs text-muted-foreground">SLA-Frist: {new Date(i.sla_deadline).toLocaleString("de-DE")}</p>}
                {i.assignee && <p className="text-xs text-muted-foreground">Disponiert an: {i.assignee}</p>}
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                {i.priority && <StatusBadge tone={CRIT_TONE[i.priority]}>{i.priority}</StatusBadge>}
                <StatusBadge tone={STATUS_TONE[i.status]}>{STATUS_LABEL[i.status]}</StatusBadge>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {i.status === "gemeldet" && <Button size="sm" variant="outline" className="gap-1.5" data-testid={`prioritize-${i.id}`} onClick={() => prioritize(i.id)}><Lightning size={15} />Priorisieren</Button>}
              {i.status === "priorisiert" && (
                <Select onValueChange={(v) => dispatch(i.id, v)}>
                  <SelectTrigger className="h-9 w-56" data-testid={`dispatch-${i.id}`}><SelectValue placeholder="Disponieren an…" /></SelectTrigger>
                  <SelectContent>{oncall.map((u) => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {i.status === "disponiert" && <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setOnsite(i.id)}><MapPin size={15} />Vor Ort / Erstsicherung</Button>}
              {i.status !== "geloest" && i.status !== "gemeldet" && <Button size="sm" className="gap-1.5" data-testid={`resolve-${i.id}`} onClick={() => resolve(i.id)}><CheckCircle size={15} />Beheben</Button>}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!onsite} onOpenChange={(o) => !o && setOnsite(null)}>
        <DialogContent><DialogHeader><DialogTitle className="font-heading">Erstsicherung vor Ort</DialogTitle></DialogHeader>
          <DialogDescription>Sofortmaßnahmen dokumentieren.</DialogDescription>
          <Textarea data-testid="onsite-measures" value={fm} onChange={(e) => setFm(e.target.value)} placeholder="Durchgeführte Erstsicherung…" />
          <DialogFooter><Button data-testid="onsite-save" onClick={doOnsite} disabled={!fm}>Speichern</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
